import { Button, TextField } from '@mui/material';
import React, { ChangeEvent, FC, FocusEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useVerifyOtp } from '../../api/mutations/reworked/use-verify-otp';
import { L } from 'harmony-language';

const initToken = Array<string>(6).fill('');

interface OtpInputProps {
    username: string;
    password: string;
    tokenSuccess: () => void;
}

// heavly inspired by https://github.com/devfolioco/react-otp-input
// if we need more features (better mobile support, paste, more keyboard shortcuts, etc) we should probably just look at including that dependency
export const OtpInput: FC<OtpInputProps> = (props) => {
    const { username, password, tokenSuccess } = props;
    const [activeInput, setActiveInput] = React.useState(0);
    const [token, setToken] = useState(initToken);
    const inputRefs = useRef<HTMLInputElement[]>([]);
    const verifyOtpMutation = useVerifyOtp(username, password);

    useEffect(() => {
        inputRefs.current[0].focus();
    }, []);

    const resetInput = () => {
        setToken(initToken);
        inputRefs.current[0].focus();
    }

    const submitToken = (token: string) => {
        verifyOtpMutation.mutate({ token: token }, {
            onSuccess: tokenSuccess,
            onError: resetInput,
        });
    }

    const isInputValueValid = (value: string) => {
        const isNumber = !isNaN(Number(value));

        return isNumber && value.trim().length === 1;
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (isInputValueValid(value)) {
            changeCode(value);
            focusInput(activeInput + 1);
        }
    };

    const handleFocus = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
        setActiveInput(index);
        e.target.select();
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            changeCode('');
            focusInput(activeInput - 1);
        }
        // react does not trigger onChange when the same value is entered again
        // so we need to focus the next input manually in this case
        else if (e.key === token[activeInput]) {
            e.preventDefault();
            focusInput(activeInput + 1);
        }
    };

    const changeCode = (value: string) => {
        const newToken = [...token];
        newToken[activeInput] = value;

        setToken(newToken);

        const auto = newToken.every(x => x !== '');
        if (auto) {
            const payload = newToken.join('');
            submitToken(payload);
        }
    }

    const focusInput = (targetIndex: number) => {
        if (inputRefs.current[targetIndex]) {
            inputRefs.current[targetIndex].focus();
            inputRefs.current[targetIndex].select();
            setActiveInput(targetIndex);
        }
    }

    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: '0fr 0fr 4rem 0fr 0fr 0fr', justifyContent: 'center', margin: '1rem 0' }}>
                {token.map((digit, i) => (
                    <TextField
                        key={i}
                        inputRef={(el) => inputRefs.current[i] = el}
                        value={digit}
                        autoComplete='off'
                        onChange={handleChange}
                        onFocus={(e) => handleFocus(e, i)}
                        onKeyDown={handleKeyDown}
                        size='small'
                        inputProps={{
                            maxLength: 1,
                            style: { textAlign: 'center' },
                            inputMode: 'numeric',
                        }}
                        sx={{ width: '40px' }}
                    />
                ))}
            </div>
            <Button
                variant='outlined'
                onClick={resetInput}
            >
                {L.clear()}
            </Button>
        </div>
    );
};
